'use client';
import React, { useEffect, useState } from 'react';
import type { FilterState } from './ProductsClient';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

const defaultCategories = ['All', 'Electronics', 'Beauty', 'Hair Care', 'Wearables', 'Food & Beverages', 'Home & Kitchen', 'Fitness', 'Books'];
const marketplaces = ['All', 'Amazon', 'Flipkart', 'Meesho', 'Nykaa'];
const ratingOptions = [
  { label: 'Any', value: 0 },
  { label: '3★ & up', value: 3 },
  { label: '4★ & up', value: 4 },
  { label: '4.5★ & up', value: 4.5 },
];

interface ProductFiltersProps {
  filters: FilterState;
  onFilterChange: (f: FilterState) => void;
  onClose: () => void;
}

export default function ProductFilters({ filters, onFilterChange, onClose }: ProductFiltersProps) {
  const [categories, setCategories] = useState<string[]>(defaultCategories);

  useEffect(() => {
    async function fetchCategories() {
      const supabase = createClient();
      try {
        const { data, error } = await supabase
          .from('product_categories')
          .select('name')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (!error && data && data.length > 0) {
          setCategories(['All', ...data.map((c) => c.name)]);
        }
      } catch (err) {
        console.log('Categories fetch error:', err);
      }
    }
    fetchCategories();
  }, []);

  const update = (partial: Partial<FilterState>) => onFilterChange({ ...filters, ...partial });

  const activeCount = [
    filters.category !== 'All',
    filters.maxReward < 5000,
    filters.marketplace !== 'All',
    filters.minRating > 0,
    filters.brand.trim() !== '',
  ].filter(Boolean).length;

  return (
    <div className="h-full overflow-y-auto p-5 space-y-7 pt-16 md:pt-0">
      {/* Mobile close */}
      <div className="flex items-center justify-between md:hidden">
        <span className="font-heading font-semibold text-foreground">Filters</span>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
          <Icon name="XMarkIcon" size={18} className="text-foreground" />
        </button>
      </div>

      {/* Active filter count */}
      {activeCount > 0 && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-primary bg-primary-container px-2.5 py-1 rounded-full">
            {activeCount} filter{activeCount > 1 ? 's' : ''} active
          </span>
          <button
            onClick={() => onFilterChange({ category: 'All', minReward: 0, maxReward: 5000, marketplace: 'All', minRating: 0, brand: '' })}
            className="text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Brand Search */}
      <div>
        <h3 className="font-heading font-semibold text-sm text-foreground mb-3">Brand</h3>
        <div className="relative">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={filters.brand}
            onChange={(e) => update({ brand: e.target.value })}
            placeholder="e.g. Samsung, Mamaearth…"
            className="w-full pl-8 pr-3 py-2 rounded-xl border border-border bg-[#F7F8FA] text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
          />
          {filters.brand && (
            <button onClick={() => update({ brand: '' })} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted">
              <Icon name="XMarkIcon" size={12} className="text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Category */}
      <div>
        <h3 className="font-heading font-semibold text-sm text-foreground mb-3">Category</h3>
        <div className="space-y-1">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => update({ category: cat })}
              className={`w-full text-left text-sm px-3 py-2 rounded-xl transition-colors ${
                filters.category === cat
                  ? 'bg-primary-container text-primary font-semibold' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Reward Range */}
      <div>
        <h3 className="font-heading font-semibold text-sm text-foreground mb-3">
          Reward: ₹{filters.minReward} – ₹{filters.maxReward}
        </h3>
        <input
          type="range"
          min={0}
          max={5000}
          step={100}
          value={filters.maxReward}
          onChange={(e) => update({ maxReward: Number(e.target.value) })}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>₹0</span>
          <span>₹5,000+</span>
        </div>
      </div>

      {/* Min Rating */}
      <div>
        <h3 className="font-heading font-semibold text-sm text-foreground mb-3">Minimum Rating</h3>
        <div className="flex flex-wrap gap-2">
          {ratingOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => update({ minRating: opt.value })}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                filters.minRating === opt.value
                  ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Marketplace */}
      <div>
        <h3 className="font-heading font-semibold text-sm text-foreground mb-3">Marketplace</h3>
        <div className="flex flex-wrap gap-2">
          {marketplaces.map((m) => (
            <button
              key={m}
              onClick={() => update({ marketplace: m })}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                filters.marketplace === m
                  ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:border-primary hover:text-primary'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Reset */}
      <button
        onClick={() => onFilterChange({ category: 'All', minReward: 0, maxReward: 5000, marketplace: 'All', minRating: 0, brand: '' })}
        className="w-full text-sm font-semibold text-primary border border-primary rounded-xl py-2 hover:bg-primary-container transition-colors"
      >
        Reset Filters
      </button>
    </div>
  );
}